import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConfirmationQueue, type ConfirmationQueue as Queue } from '../../api/client';
import { Icon } from '../../components/Icon';
import { Pending, SeverityBadge, Stat, gapLabel, gapSeverity } from './shared';

const CATEGORIES: Array<{ title: string; fields: string[] }> = [
  { title: 'Employee information', fields: ['designation', 'employment_type', 'date_of_joining', 'work_arrangement'] },
  { title: 'Organization', fields: ['reporting_manager', 'department'] },
  { title: 'Job', fields: ['job_description', 'kpis'] },
];

/**
 * Every row here is a question for a person. The screen deliberately offers no
 * way to make an item disappear except by supplying the real answer.
 */
export function ConfirmationQueue() {
  const [queue, setQueue] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [field, setField] = useState('');
  const [severity, setSeverity] = useState('');

  useEffect(() => {
    getConfirmationQueue()
      .then(setQueue)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    if (!queue) return [];
    const flat = queue.employees.flatMap((e) =>
      e.gaps.map((g) => ({
        employeeId: e.employeeId, employeeCode: e.employeeCode, name: e.name,
        designation: e.designation, gap: g,
        severity: (queue.priority?.[g] ?? gapSeverity(g)) as 'critical' | 'high' | 'medium',
        owner: queue.owners?.[g] ?? 'Pending assignment',
      })));
    return flat
      .filter((r) => !field || r.gap === field)
      .filter((r) => !severity || r.severity === severity)
      .sort((a, b) => {
        const order = { critical: 0, high: 1, medium: 2 } as const;
        return order[a.severity] - order[b.severity] || a.name.localeCompare(b.name);
      });
  }, [queue, field, severity]);

  const critical = useMemo(
    () => (queue?.employees ?? []).flatMap((e) => e.gaps).filter((g) => gapSeverity(g) === 'critical').length,
    [queue],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="audit" size="xs" /><span>Organization</span></span>
          <h1>Confirmation Queue</h1>
        </div>
        <div className="toolbar">
          <select aria-label="Filter by field" value={field} onChange={(e) => setField(e.target.value)}>
            <option value="">Every field</option>
            {Object.keys(queue?.byField ?? {}).map((f) => (
              <option key={f} value={f}>{gapLabel(f)}</option>
            ))}
          </select>
          <select aria-label="Filter by severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">Any severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
          </select>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="org-stats">
        <Stat label="Open items" value={queue?.totalOpenItems ?? '—'} tone={queue && queue.totalOpenItems > 0 ? 'warn' : 'ok'} />
        <Stat label="Critical" value={critical} tone={critical > 0 ? 'bad' : 'ok'} hint="Blocks approval routing" />
        <Stat label="People affected" value={queue?.employees.filter((e) => e.openItems > 0).length ?? '—'} />
        <Stat label="Missing job descriptions" value={queue?.byField.job_description ?? 0} />
      </div>

      {queue?.legalIdentity && queue.legalIdentity.status !== 'confirmed' && (
        <div className="banner warn" style={{ margin: '14px 0 0' }}>
          <strong>Legal identity — {queue.legalIdentity.missingFields.length} field(s) unsupplied.</strong>{' '}
          Owner: {queue.legalIdentity.owner}. Blocks: {queue.legalIdentity.blocks}.
        </div>
      )}

      <div className="banner info" style={{ margin: '14px 0' }}>
        {queue?.note ?? 'Missing information is flagged, never filled in.'}
      </div>

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>{loading ? 'Loading…' : `${rows.length} open item${rows.length === 1 ? '' : 's'}`}</strong>
            <span className="muted" style={{ fontSize: 12 }}>Answered by a person, not by the system</span>
          </div>
          {!loading && rows.length === 0 ? (
            <div className="empty">
              <strong>Nothing outstanding under these filters</strong>
              <p>Clear the filters to see the rest of the queue.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Code</th>
                    <th>Employee</th>
                    <th>Missing</th>
                    <th style={{ width: 170 }}>Who confirms</th>
                    <th style={{ width: 110 }}>Priority</th>
                    <th style={{ width: 140 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.employeeId}-${r.gap}`}>
                      <td className="mono">{r.employeeCode}</td>
                      <td>
                        <strong>{r.name}</strong>
                        <div className="muted" style={{ fontSize: 12 }}><Pending>{r.designation}</Pending></div>
                      </td>
                      <td>{gapLabel(r.gap)}</td>
                      <td className="muted">{r.owner}</td>
                      <td><SeverityBadge level={r.severity} /></td>
                      <td>
                        {r.gap === 'job_description'
                          ? <Link className="record-link" to="/org/job-descriptions">Draft it</Link>
                          : r.gap === 'work_arrangement'
                            ? <Link className="record-link" to="/org/work-arrangements">Record it</Link>
                            : <Link className="record-link" to={`/employees/${r.employeeId}`}>Open record</Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="org-side">
          {CATEGORIES.map((cat) => (
            <div className="panel" key={cat.title}>
              <div className="panel-head"><strong>{cat.title}</strong></div>
              <ul className="org-plain-list">
                {cat.fields.map((f) => (
                  <li key={f}>
                    <span>{gapLabel(f)}</span>
                    <span className={`org-count${(queue?.byField[f] ?? 0) > 0 ? ' org-count-warn' : ''}`}>
                      {queue?.byField[f] ?? 0}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
